import { useState, useEffect } from 'react';
import { Phone, Clock, Calendar, Search, Filter, Download, Play, FileText, User, MapPin, LogOut, Users as UsersIcon, X, Settings, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { useLogout, useGetIdentity } from '@refinedev/core';
import CallDetails from '../components/CallDetails';
import Stats from '../components/Stats';

const API_BASE_URL = '/api';

export default function Dashboard() {
  const { mutate: logout } = useLogout();
  const { data: identity } = useGetIdentity();
  
  const [calls, setCalls] = useState([]);
  const [filteredCalls, setFilteredCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWorkflow, setSelectedWorkflow] = useState('all');
  const [selectedCall, setSelectedCall] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [expandedCallId, setExpandedCallId] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [transcriptData, setTranscriptData] = useState(null);
  const [transcriptContent, setTranscriptContent] = useState('');
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [displayConfigs, setDisplayConfigs] = useState({});
  
  // Pagination state
  const [totalCalls, setTotalCalls] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 100;
  
  // Date filter state
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [exporting, setExporting] = useState(false);

  // Fetch calls from API
  useEffect(() => {
    fetchCalls();
    fetchWorkflows();
  }, []);
  
  // Refetch when date filters change
  useEffect(() => {
    if (calls.length > 0 || fromDate || toDate) {
      fetchCalls();
    }
  }, [fromDate, toDate]);

  const fetchCalls = async (loadMore = false) => {
    try {
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setOffset(0);
      }
      
      const currentOffset = loadMore ? offset : 0;
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('offset', currentOffset);
      
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      
      console.log('Fetching calls from:', `/api/calls?${params.toString()}`);
      const response = await axios.get(`/api/calls?${params.toString()}`);
      console.log('Response:', response.data);
      
      // Handle paginated response
      const { data, total, has_more } = response.data;
      
      if (loadMore) {
        // Append to existing calls
        setCalls(prev => [...prev, ...data]);
        setFilteredCalls(prev => [...prev, ...data]);
        setOffset(currentOffset + limit);
      } else {
        // Replace calls
        setCalls(data);
        setFilteredCalls(data);
        setOffset(limit);
      }
      
      setTotalCalls(total);
      setHasMore(has_more);
    } catch (error) {
      console.error('Error fetching calls:', error);
      console.error('Error details:', error.response || error.message);
      alert(`Error loading calls: ${error.message}. Check console for details.`);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchWorkflows = async () => {
    try {
      const response = await axios.get(`/api/calls/workflows/list`);
      setWorkflows(response.data);
      
      // Fetch display configs for all workflows
      const configs = {};
      for (const workflow of response.data) {
        try {
          const configResponse = await axios.get(`/api/display-config/workflows/${workflow.workflow_id}`);
          if (configResponse.data && configResponse.data.length > 0) {
            configs[workflow.workflow_id] = configResponse.data;
          }
        } catch (error) {
          // No config for this workflow, skip
          console.log(`No display config for workflow ${workflow.workflow_id}`);
        }
      }
      setDisplayConfigs(configs);
    } catch (error) {
      console.error('Error fetching workflows:', error);
    }
  };

  // Delete call function
  const handleDeleteCall = async (callId) => {
    try {
      await axios.delete(`/api/calls/${callId}`);
      // Remove from local state
      setCalls(calls.filter(call => call.id !== callId));
      setDeleteConfirmId(null);
      alert('Call deleted successfully');
    } catch (error) {
      console.error('Error deleting call:', error);
      alert(error.response?.data?.detail || 'Failed to delete call');
    }
  };

  // Filter calls based on search and workflow
  useEffect(() => {
    let filtered = calls;

    // Filter by workflow
    if (selectedWorkflow !== 'all') {
      filtered = filtered.filter(call => call.workflow_id === selectedWorkflow);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(call => {
        const searchLower = searchTerm.toLowerCase();
        return (
          call.workflow_run_id?.toLowerCase().includes(searchLower) ||
          call.workflow_name?.toLowerCase().includes(searchLower) ||
          JSON.stringify(call.gathered_context)?.toLowerCase().includes(searchLower) ||
          JSON.stringify(call.initial_context)?.toLowerCase().includes(searchLower)
        );
      });
    }

    setFilteredCalls(filtered);
  }, [searchTerm, selectedWorkflow, calls]);

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const getCustomerName = (call) => {
    try {
      const context = typeof call.gathered_context === 'string' 
        ? JSON.parse(call.gathered_context) 
        : call.gathered_context;
      return context?.customer_name || 'Unknown';
    } catch {
      return 'Unknown';
    }
  };

  const formatDisposition = (disposition) => {
    const dispositionMap = {
      'pipeline_error': 'PE',
      'user_hangup': 'UH',
      'user_qualified': 'UQ',
      'user_not_qualified': 'UNQ',
      'user_callback': 'CB',
      'user_busy': 'UB',
      'user_no_answer': 'UNA',
      'system_error': 'SE',
      'user_idle_max_duration_exceeded': 'UIMDE',
    };
    
    if (!disposition) return 'N/A';
    return dispositionMap[disposition.toLowerCase()] || disposition;
  };

  const getCallDisposition = (call) => {
    try {
      const context = typeof call.gathered_context === 'string' 
        ? JSON.parse(call.gathered_context) 
        : call.gathered_context;
      const disposition = context?.call_disposition || context?.mapped_call_disposition;
      return formatDisposition(disposition);
    } catch {
      return 'N/A';
    }
  };

  const toggleAudioPlayer = (callId) => {
    setExpandedCallId(expandedCallId === callId ? null : callId);
  };

  const openTranscript = async (call) => {
    setTranscriptData(call);
    setShowTranscript(true);
    setLoadingTranscript(true);
    setTranscriptContent('');

    if (call.transcript_url) {
      try {
        const url = getFullUrl(call.transcript_url);
        const response = await fetch(url);
        const text = await response.text();
        
        // Parse and format the transcript
        const lines = text.split('\n');
        const formattedLines = lines
          .filter(line => line.trim()) // Remove empty lines
          .map(line => {
            // Remove timestamp pattern [2026-02-15T09:48:50.308+00:00]
            const withoutTimestamp = line.replace(/^\[[\d\-T:+.]+\]\s*/, '');
            
            // Format speaker labels
            if (withoutTimestamp.startsWith('assistant:')) {
              return `🤖 Agent: ${withoutTimestamp.replace('assistant:', '').trim()}`;
            } else if (withoutTimestamp.startsWith('user:')) {
              return `👤 Customer: ${withoutTimestamp.replace('user:', '').trim()}`;
            }
            return withoutTimestamp;
          })
          .join('\n\n'); // Add spacing between lines
        
        setTranscriptContent(formattedLines || text);
      } catch (error) {
        console.error('Error fetching transcript:', error);
        setTranscriptContent('Failed to load transcript. Please try downloading it instead.');
      } finally {
        setLoadingTranscript(false);
      }
    } else {
      setLoadingTranscript(false);
    }
  };

  const getFullUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `https://${url}`;
  };

  // Get nested value from object using path (e.g., "gathered_context.customer_name")
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  // Format value based on field type
  const formatFieldValue = (value, fieldType) => {
    if (!value) return null;
    
    switch (fieldType) {
      case 'phone':
        // Format phone number
        const phone = String(value).replace(/\D/g, '');
        if (phone.length === 10) {
          return `+91-${phone.slice(0, 5)}-${phone.slice(5)}`;
        }
        return value;
      
      case 'currency':
        // Format currency
        return `₹${value}`;
      
      case 'email':
        return value;
      
      case 'datetime':
      case 'timestamp':
        // Format datetime/timestamp
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            return date.toLocaleString('en-IN', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            });
          }
        } catch (e) {
          // If parsing fails, return as-is
        }
        return value;
      
      default:
        // Truncate long text
        const str = String(value);
        return str.length > 40 ? str.substring(0, 37) + '...' : str;
    }
  };

  // Get display fields for a call
  const openExportModal = () => {
    // Set default date range (last 30 days)
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setExportToDate(today.toISOString().split('T')[0]);
    setExportFromDate(thirtyDaysAgo.toISOString().split('T')[0]);
    setShowExportModal(true);
  };
  
  const handleExportCSV = async () => {
    try {
      setExporting(true);
      
      // Validate date range
      const from = new Date(exportFromDate);
      const to = new Date(exportToDate);
      const diffDays = Math.ceil((to - from) / (1000 * 60 * 60 * 24));
      
      if (diffDays > 90) {
        alert('Date range cannot exceed 90 days');
        return;
      }
      
      if (diffDays < 0) {
        alert('From date must be before to date');
        return;
      }
      
      // Build export URL with params
      const params = new URLSearchParams();
      params.append('from_date', exportFromDate);
      params.append('to_date', exportToDate);
      if (selectedWorkflow !== 'all') {
        params.append('workflow_id', selectedWorkflow);
      }
      
      const url = `/api/export/csv?${params.toString()}`;
      
      // Use axios for download (includes auth token automatically)
      const response = await axios.get(url, {
        responseType: 'blob',
      });
      
      // Get filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'calls_export.csv';
      if (contentDisposition) {
        const matches = /filename="?([^"]+)"?/.exec(contentDisposition);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }
      
      // Download file
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      
      setShowExportModal(false);
      alert('CSV exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      alert(`Export failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setExporting(false);
    }
  };
  
  const getDisplayFields = (call) => {
    const config = displayConfigs[call.workflow_id];
    if (!config || config.length === 0) {
      // No config, return default fields
      return [];
    }

    return config
      .filter(field => field.show_in_list)
      .map(field => {
        const value = getNestedValue(call, field.field_path);
        if (!value) return null;

        return {
          icon: field.icon,
          label: field.display_label,
          value: formatFieldValue(value, field.field_type),
          rawValue: value
        };
      })
      .filter(Boolean);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Call Dashboard</h1>
                <p className="text-sm text-gray-500">Monitor and analyze your voice agent calls</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* Admin Links */}
              {identity?.role === 'admin' && (
                <>
                  <Link
                    to="/agent-config"
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Agent Config</span>
                  </Link>
                  <Link
                    to="/users"
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
                  >
                    <UsersIcon className="w-4 h-4" />
                    <span>Manage Users</span>
                  </Link>
                </>
              )}
              
              {/* User Info */}
              {identity && (
                <div className="flex items-center space-x-3 px-4 py-2 bg-gray-50 rounded-lg">
                  <img
                    src={identity.avatar}
                    alt={identity.name}
                    className="w-8 h-8 rounded-full"
                  />
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">{identity.name}</p>
                    <p className="text-xs text-gray-500 capitalize">{identity.role}</p>
                  </div>
                </div>
              )}
              
              {/* Refresh Button */}
              <button
                onClick={fetchCalls}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Refresh</span>
              </button>
              
              {/* Logout Button */}
              <button
                onClick={() => logout()}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <Stats calls={calls} />

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search calls..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Agent Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={selectedWorkflow}
                onChange={(e) => setSelectedWorkflow(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="all">All Agents</option>
                {workflows.map(workflow => (
                  <option key={workflow.id} value={workflow.workflow_id}>
                    {workflow.workflow_name || `Agent ${workflow.workflow_id}`}
                  </option>
                ))}
              </select>
            </div>
            
            {/* From Date */}
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="date"
                placeholder="From Date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* To Date */}
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="date"
                placeholder="To Date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          {/* Clear Filters Button */}
          {(fromDate || toDate) && (
            <div className="mt-4">
              <button
                onClick={() => {
                  setFromDate('');
                  setToDate('');
                }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear Date Filters
              </button>
            </div>
          )}
        </div>

        {/* Calls List */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Recent Calls ({filteredCalls.length} of {totalCalls})
            </h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredCalls.length === 0 ? (
            <div className="text-center py-12">
              <Phone className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No calls found</h3>
              <p className="mt-1 text-sm text-gray-500">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredCalls.map((call) => (
                <div key={call.id} className="transition-colors">
                  <div
                    onClick={() => setSelectedCall(call)}
                    className="px-6 py-4 hover:bg-gray-50 cursor-pointer"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-blue-600" />
                            </div>
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-gray-900">
                              {getCustomerName(call)}
                            </h3>
                            <p className="text-sm text-gray-500">
                              Run ID: {call.workflow_run_id}
                            </p>
                          </div>
                        </div>

                        {/* Dynamic Fields Row */}
                        {getDisplayFields(call).length > 0 && (
                          <div className="flex flex-wrap items-center gap-3 text-sm mb-2">
                            {getDisplayFields(call).map((field, index) => (
                              <div key={index} className="flex items-center space-x-1">
                                <span className="text-base">{field.icon}</span>
                                <span className="text-gray-600">{field.label}:</span>
                                <span className="text-gray-900 font-medium">{field.value}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDistanceToNow(new Date(call.created_at), { addSuffix: true })}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>{formatDuration(call.call_duration_seconds)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              getCallDisposition(call) === 'user_hangup' 
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {getCallDisposition(call)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirmId(call.id);
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete call"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                        {call.recording_url && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleAudioPlayer(call.id);
                            }}
                            className={`p-2 rounded-lg transition-colors ${
                              expandedCallId === call.id
                                ? 'bg-blue-100 text-blue-700'
                                : 'text-blue-600 hover:bg-blue-50'
                            }`}
                            title={expandedCallId === call.id ? "Hide Player" : "Play Recording"}
                          >
                            <Play className="w-5 h-5" />
                          </button>
                        )}
                        {call.transcript_url && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openTranscript(call);
                            }}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="View Transcript"
                          >
                            <FileText className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expandable Audio Player */}
                  {expandedCallId === call.id && call.recording_url && (
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                            <Play className="w-6 h-6 text-white" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 mb-2">
                            Recording - {getCustomerName(call)}
                          </p>
                          <audio
                            controls
                            className="w-full"
                            style={{ height: '40px' }}
                            src={getFullUrl(call.recording_url)}
                          >
                            Your browser does not support the audio element.
                          </audio>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Load More Button */}
          {hasMore && !loading && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => fetchCalls(true)}
                disabled={loadingMore}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {loadingMore ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    <span>Load More Calls ({totalCalls - filteredCalls.length} remaining)</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Call Details Modal */}
      {selectedCall && (
        <CallDetails
          call={selectedCall}
          onClose={() => setSelectedCall(null)}
          displayConfig={displayConfigs[selectedCall.workflow_id] || []}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Call</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this call? All call data including recordings and transcripts will be permanently removed.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteCall(deleteConfirmId)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export CSV Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Download className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Export Calls to CSV</h3>
                <p className="text-sm text-gray-500">Select date range to export</p>
              </div>
            </div>
            
            <div className="space-y-4 mb-6">
              {/* From Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  From Date
                </label>
                <input
                  type="date"
                  value={exportFromDate}
                  onChange={(e) => setExportFromDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              
              {/* To Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To Date
                </label>
                <input
                  type="date"
                  value={exportToDate}
                  onChange={(e) => setExportToDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              
              {/* Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Maximum date range is 90 days. Default is last 30 days.
                  {selectedWorkflow !== 'all' && (
                    <span className="block mt-1">Exporting for: <strong>{workflows.find(w => w.workflow_id === selectedWorkflow)?.workflow_name || selectedWorkflow}</strong></span>
                  )}
                </p>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowExportModal(false)}
                disabled={exporting}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleExportCSV}
                disabled={exporting}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {exporting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Exporting...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    <span>Download CSV</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Transcript Modal */}
      {showTranscript && transcriptData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-gray-700 to-gray-800 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3 text-white">
                <FileText className="w-6 h-6" />
                <div>
                  <h2 className="text-xl font-semibold">Call Transcript</h2>
                  <p className="text-sm text-gray-300">{getCustomerName(transcriptData)} - Run ID: {transcriptData.workflow_run_id}</p>
                </div>
              </div>
              <button
                onClick={() => setShowTranscript(false)}
                className="text-white hover:bg-gray-700 p-2 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6">
              <div className="prose max-w-none">
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Duration:</span>
                      <span className="ml-2 text-gray-900">{formatDuration(transcriptData.call_duration_seconds)}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Date:</span>
                      <span className="ml-2 text-gray-900">{formatDistanceToNow(new Date(transcriptData.created_at), { addSuffix: true })}</span>
                    </div>
                  </div>
                </div>

                {transcriptData.transcript_url ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Transcript</h3>
                      <a
                        href={getFullUrl(transcriptData.transcript_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 text-sm flex items-center space-x-1"
                      >
                        <span>Download</span>
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                    
                    {loadingTranscript ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600"></div>
                      </div>
                    ) : transcriptContent ? (
                      <div className="bg-white border border-gray-200 rounded-lg p-6 max-h-96 overflow-y-auto">
                        <div className="whitespace-pre-wrap text-sm text-gray-800 leading-relaxed text-left space-y-2">
                          {transcriptContent}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <p className="text-yellow-800 text-sm">
                          Unable to display transcript inline. Please use the download button above to view the full transcript.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">No transcript available for this call</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
